/**
 * ヘッダーコンポーネント
 * レスポンシブ・ダークモード対応
 */

import React from 'react';
import {
  Box,
  Group,
  ActionIcon,
  Badge,
  Avatar,
  Menu,
  Text,
  Indicator,
  Tooltip,
  Button,
  Burger,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import {
  IconMenu2,
  IconBell,
  IconMessage,
  IconSettings,
  IconUser,
  IconLogout,
  IconCreditCard,
  IconSun,
  IconMoon,
  IconSparkles,
  IconHelp,
  IconSpeakerphone,
  IconDeviceDesktop,
  IconBellRinging,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useAnnouncements } from '@/features/announcements/api/hooks';
import { AnnouncementModal } from '@/features/announcements/components/AnnouncementModal';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useNFC } from '../../nfc/contexts/NFCContext';
import { useTheme } from '../../theme/components/ThemeProvider';
import { useGlassmorphism } from '@/contexts/GlassmorphismContext';
import { useBulletinStatistics } from '@/features/bulletinBoard/api/hooks';
import { useChatRoomList } from '@/features/chat/hooks';
import { useNfcNotificationMode } from '@/shared/hooks/useNfcNotificationMode';

interface HeaderProps {
  onMenuClick: () => void;
  title?: string;
  icon?: React.ReactNode;
  breadcrumbs?: { label: string; path?: string }[];
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick, title, icon, breadcrumbs }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isConnected: nfcConnected } = useNFC();
  const { colorScheme, toggleColorScheme, isLoading: themeLoading } = useTheme();
  const { isEnabled: glassmorphismEnabled, toggle: toggleGlassmorphism } = useGlassmorphism();
  const { mode: nfcNotificationMode, toggleMode: toggleNfcNotificationMode } =
    useNfcNotificationMode();
  const theme = useMantineTheme();
  const isDark = colorScheme === 'dark';

  // システムお知らせ
  const [announcementModalOpened, setAnnouncementModalOpened] = useState(false);
  const { data: announcementData } = useAnnouncements();
  const unreadAnnouncements = announcementData?.unread_count ?? 0;
  const hasShownAutoModal = useRef(false);

  // 未読の重要お知らせがあればログイン後に自動表示
  useEffect(() => {
    if (
      !hasShownAutoModal.current &&
      announcementData?.unread_important &&
      announcementData.unread_important.length > 0
    ) {
      hasShownAutoModal.current = true;
      setAnnouncementModalOpened(true);
    }
  }, [announcementData?.unread_important]);

  // 掲示板未読数取得
  const { data: bulletinStats } = useBulletinStatistics();
  const unreadBulletins = bulletinStats?.unread_bulletins || 0;

  // チャット未読数取得
  const { data: chatRooms } = useChatRoomList({
    filters: { is_active: true },
  });

  const unreadMessages =
    chatRooms?.data
      ?.filter((room) => (room.unread_count ?? 0) > 0)
      .reduce((sum, room) => sum + (room.unread_count ?? 0), 0) || 0;

  return (
    <Box
      h={48}
      px={{ base: 'sm', md: 'lg' }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: isDark
          ? 'linear-gradient(135deg, rgba(26, 27, 30, 0.98) 0%, rgba(30, 41, 59, 0.95) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(240, 249, 255, 0.95) 100%)',
        backdropFilter: 'blur(10px)',
        borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
        zIndex: 99,
        boxShadow: theme.shadows.xl,
      }}
    >
      <Group h="100%" justify="space-between">
        {/* 左側エリア */}
        <Group>
          {/* モバイル用ハンバーガーメニュー */}
          <Box display={{ base: 'block', md: 'none' }}>
            <Burger
              opened={false}
              onClick={onMenuClick}
              size="sm"
              color={isDark ? theme.colors.gray[0] : theme.colors.dark[6]}
            />
          </Box>

          {/* デスクトップ用メニューアイコン */}
          <Box display={{ base: 'none', md: 'block' }}>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={onMenuClick}
              style={{
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <IconMenu2 size={20} />
            </ActionIcon>
          </Box>

          {/* ページタイトル（アイコン付き） */}
          {title && (
            <Group
              gap="xs"
              ml={{ base: 5, md: 180 }} // デスクトップでは180px右に寄せる
            >
              {icon && (
                <Box
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    color: isDark ? theme.colors.blue[4] : theme.colors.blue[6],
                  }}
                >
                  {icon}
                </Box>
              )}
              <Text
                size={{ base: 'sm', md: 'md' }}
                fw={600}
                c={isDark ? theme.colors.gray[0] : theme.colors.dark[8]}
                style={{
                  lineHeight: 1.2,
                }}
              >
                {title}
              </Text>
            </Group>
          )}
        </Group>

        {/* 右側エリア */}
        <Group gap={{ base: 'xs', md: 'md' }}>
          {/* メッセージアイコン - チャット未読数（デスクトップのみ） */}
          <Box display={{ base: 'none', md: 'block' }}>
            <Tooltip label="チャット">
              <Indicator
                label={unreadMessages}
                size={16}
                disabled={unreadMessages === 0}
                color="red"
                processing={unreadMessages > 0}
                position="bottom-end"
              >
                <ActionIcon variant="subtle" size="lg" onClick={() => navigate('/chat')}>
                  <IconMessage size={20} />
                </ActionIcon>
              </Indicator>
            </Tooltip>
          </Box>

          {/* マニュアルリンク */}
          <Tooltip label="操作マニュアル">
            <ActionIcon
              variant="subtle"
              size="lg"
              component="a"
              href={`${window.location.origin}/manual/reception-guide.html`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconHelp size={20} />
            </ActionIcon>
          </Tooltip>

          {/* システムお知らせアイコン */}
          <Tooltip label="システムお知らせ">
            <Indicator
              label={unreadAnnouncements}
              size={16}
              disabled={unreadAnnouncements === 0}
              color="blue"
              processing={unreadAnnouncements > 0}
              position="bottom-end"
            >
              <ActionIcon
                variant="subtle"
                size="lg"
                onClick={() => setAnnouncementModalOpened(true)}
              >
                <IconSpeakerphone size={20} />
              </ActionIcon>
            </Indicator>
          </Tooltip>

          {/* 通知アイコン - 掲示板未読数 */}
          <Tooltip label="掲示板">
            <Indicator
              label={unreadBulletins}
              size={16}
              disabled={unreadBulletins === 0}
              color="red"
              processing={unreadBulletins > 0}
              position="bottom-end"
            >
              <ActionIcon variant="subtle" size="lg" onClick={() => navigate('/bulletin-boards')}>
                <IconBell size={20} />
              </ActionIcon>
            </Indicator>
          </Tooltip>

          {/* NFC通知モード切替 */}
          <Tooltip
            label={
              nfcNotificationMode === 'modal'
                ? 'NFC通知: モーダル表示中（クリックでデスクトップ通知に切替）'
                : 'NFC通知: デスクトップ通知中（クリックでモーダルに切替）'
            }
          >
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={toggleNfcNotificationMode}
              color={nfcNotificationMode === 'notification' ? 'blue' : undefined}
            >
              {nfcNotificationMode === 'notification' ? (
                <IconBellRinging size={20} />
              ) : (
                <IconDeviceDesktop size={20} />
              )}
            </ActionIcon>
          </Tooltip>

          {/* ユーザーメニュー */}
          <Menu shadow="md" width={200} position="bottom-end">
            <Menu.Target>
              <Button
                variant="subtle"
                leftSection={
                  <Avatar
                    src={user?.photo_url || user?.photo_path}
                    size={32}
                    radius="xl"
                    style={{
                      border: `2px solid ${user?.gender === 'female' ? '#ff69b4' : '#4169e1'}`,
                    }}
                  >
                    {!user?.photo_url && !user?.photo_path && user?.name?.[0]?.toUpperCase()}
                  </Avatar>
                }
                rightSection={
                  <Badge
                    size="sm"
                    color={
                      user?.role === 'admin' ? 'red' : user?.role === 'manager' ? 'orange' : 'blue'
                    }
                    variant="dot"
                  >
                    {user?.role === 'admin'
                      ? '管理者'
                      : user?.role === 'manager'
                        ? 'マネージャー'
                        : 'スタッフ'}
                  </Badge>
                }
              >
                {user?.name}
              </Button>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Label>アカウント</Menu.Label>
              <Menu.Item
                leftSection={<IconUser size={16} />}
                onClick={() => navigate('/mypage/profile')}
              >
                プロフィール
              </Menu.Item>
              <Menu.Item
                leftSection={<IconSettings size={16} />}
                onClick={() => navigate('/mypage/settings')}
              >
                設定
              </Menu.Item>

              <Menu.Divider />

              <Menu.Label>表示設定</Menu.Label>
              <Menu.Item
                leftSection={
                  colorScheme === 'dark' ? <IconSun size={16} /> : <IconMoon size={16} />
                }
                onClick={toggleColorScheme}
                disabled={themeLoading}
                color={colorScheme === 'dark' ? 'blue' : undefined}
              >
                {colorScheme === 'dark' ? 'ライトモード' : 'ダークモード'}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconSparkles size={16} />}
                onClick={toggleGlassmorphism}
                color={glassmorphismEnabled ? 'cyan' : undefined}
              >
                グラスモーフィズム
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCreditCard size={16} />}
                color={nfcConnected ? 'green' : undefined}
              >
                NFC {nfcConnected ? '接続中' : '未接続'}
              </Menu.Item>

              <Menu.Divider />

              <Menu.Item color="red" leftSection={<IconLogout size={16} />} onClick={logout}>
                ログアウト
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {/* システムお知らせモーダル */}
      <AnnouncementModal
        opened={announcementModalOpened}
        onClose={() => setAnnouncementModalOpened(false)}
      />
    </Box>
  );
};
